import axiosInstance from "../../../axios";

const suppressErrors = {suppressErrors: true};

// ****************** WORKING WITH SIGNING CONTRACTS IN ACTIVE (CURRENT) ORGANIZATION ******************

export const getSignContracts = async (contractParams) => {
  const params = {...contractParams};
  const {data} = await axiosInstance.get("signing/contracts", {params});
  return data;
};

export const createSignContract = () =>
  axiosInstance.post("signing/contracts").then((res) => res.data);

export const getSignContract = (id) =>
  axiosInstance.get(`signing/contracts/${id}`).then((res) => res.data);

export const updateSignContract = (id, payload) =>
  axiosInstance.put(`signing/contracts/${id}`, payload).then((res) => res.data);

export const deleteSignContract = (id) =>
  axiosInstance.delete(`signing/contracts/${id}`).then((res) => res.data);

export const changeStatusSignContract = (id, status) =>
  axiosInstance.post(`signing/contracts/${id}/status`, {status}).then((res) => res.data);

export const getFileUID = (file) =>
  axiosInstance.post("signing/contracts/file-upload", file).then((res) => res.data);

export const downloadSignContractPDF = (id) =>
  axiosInstance.get(`signing/contracts/${id}/download`).then((res) => res.data);

// ****************** WORKING WITH MEMBERS INTO SIGNING CONTRACT ******************

export const createSignContractMember = (id, payload) =>
  axiosInstance
    .post(`signing/contracts/${id}/participants`, payload)
    .then((res) => res.data);

export const checkEmailSignContractMember = (email) =>
  axiosInstance.post("signing/contracts/check-email", {email}).then((res) => res.data);

export const getOrganizationForSignContractMember = async (
  name,
  companyNumber,
  extraData = "no-extra-data"
) => {
  const params = {name, companyNumber, extraData, size: 10000};
  const {data} = await axiosInstance.get("organization/search", {params});
  return data.organizations.slice().sort((a, b) => (a.name > b.name ? 1 : -1));
};

export const getOrganizationMembersForSignContractMember = (id) =>
  axiosInstance.get(`signing/organization/${id}/members`).then((res) => res.data);

export const getSignContractMember = (id, memberId) =>
  axiosInstance
    .get(`signing/contracts/${id}/participants/${memberId}`)
    .then((res) => res.data);

export const deleteSignContractMember = (id, memberId) =>
  axiosInstance
    .delete(`signing/contracts/${id}/participants/${memberId}`)
    .then((res) => res.data);

export const patchSignContractParticipant = (contractId, participantId, payload) =>
  axiosInstance
    .patch(`signing/contracts/${contractId}/participants/${participantId}`, payload)
    .then((res) => res.data);

const participantAuthenticateBase = (contractId, participantId, emailToken) => {
  const prefix = emailToken ? "public/signing" : "signing";
  const params = emailToken ? {emailToken} : undefined;
  return {
    url: `${prefix}/contracts/${contractId}/participants/${participantId}/authenticate`,
    params,
  };
};
const participantPost = (cId, pId, emailToken, path, body = {}, config = {}) => {
  const {url, params} = participantAuthenticateBase(cId, pId, emailToken);
  return axiosInstance
    .post(`${url}/${path}`, body, {params, ...config})
    .then((res) => res.data);
};

const participantMethod =
  (path, config = {}) =>
  (cId, pId, token, body = {}) =>
    participantPost(cId, pId, token, path, body, config);

// operations with BankID
export const prepareHsParticipantBankIdAuth = participantMethod("bankid/prepare");
// polling and cancelling are handled by the caller: an order that is gone (collected,
// cancelled, timed out) is an expected outcome and must not raise an error notification
export const pollHsParticipantBankIdQrCode = participantMethod(
  "bankid/qrcode-data",
  suppressErrors
);
export const cancelHsParticipantBankIdAuth = participantMethod(
  "bankid/cancel",
  suppressErrors
);
export const completeHsParticipantBankIdAuth = participantMethod("bankid/complete");

// operations with SMS
export const sendHsParticipantSmsAuth = participantMethod("sms/send", suppressErrors);
export const verifyHsParticipantSmsAuth = participantMethod("sms/verify", suppressErrors);
export const cancelHsParticipantSmsAuth = participantMethod("sms/cancel");

export const remindSignContractMember = (id, memberId) =>
  axiosInstance
    .post(`signing/contracts/${id}/participants/${memberId}/remind`)
    .then((res) => res.data);

export const changeSignOrder = (contractId, participantId, signOrder) =>
  axiosInstance
    .post(
      `signing/contracts/${contractId}/participants/${participantId}/sign-order-change`,
      {signOrder}
    )
    .then((res) => res.data);

export const signSignContractMember = (id) =>
  axiosInstance.post(`signing/contracts/${id}/sign`).then((res) => res.data);

export const generateSignContractFile = (id, values) =>
  axiosInstance
    .post(`signing/contracts/${id}/generate-pdf`, values)
    .then((res) => res.data);

// ****************** WORKING WITH SIGNING CONTRACTS FROM PUBLIC ZONE ******************

export const checkPublicToken = (params) =>
  axiosInstance
    .get("public/signing/get-member-data-by-token", {
      params,
    })
    .then((res) => res.data);

export const getPublicSignContract = async (id, emailToken) => {
  const params = {emailToken};
  const {data} = await axiosInstance.get(`public/signing/contracts/${id}`, {params});
  return data;
};

export const signSignPublicContractMember = (id, emailToken) =>
  axiosInstance
    .post(`public/signing/contracts/${id}/sign?emailToken=${emailToken}`)
    .then((res) => res.data);

export const downloadPublicSignContractPDF = (id, emailToken) =>
  axiosInstance
    .get(`public/signing/contracts/${id}/download?emailToken=${emailToken}`)
    .then((res) => res.data);

// ****************** WORKING WITH INVOICE PROFILES ******************
export const getInvoiceProfiles = async (values) => {
  const params = {...values};
  const {data} = await axiosInstance.get("signing/invoice-profiles", {params});
  return data;
};

export const getRecentlyInvoiceProfiles = async () => {
  const {data} = await axiosInstance.get("signing/invoice-profiles-recently");
  return data;
};

export const createInvoiceProfile = (values) =>
  axiosInstance.post("signing/invoice-profiles", values).then((res) => res.data);

export const getInvoiceProfile = async (id) => {
  const {data} = await axiosInstance.get(`signing/invoice-profiles/${id}`);
  return data;
};

export const updateInvoiceProfile = (id, payload) =>
  axiosInstance.put(`signing/invoice-profiles/${id}`, payload).then((res) => res.data);

export const deleteInvoicingProfile = (id) =>
  axiosInstance.delete(`signing/invoice-profiles/${id}`).then((res) => res.data);
